import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  ReactSelect,
  Input,
  toastEmitter,
  Icon,
} from "@anchor/react-components/dist/lib/components";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { postAPI } from "../../Services/APIservices";
import { CardItem } from "./Components/SimulationScenarioCard";
import "./SenerioRequest.scss";

interface IPROPS {
  isOpen?: boolean;
  dropDownData?: any;
  onClose?: any;
  onSuccess: () => void;
  existingData?: CardItem; // New
}

interface InputField {
  conditionId?: number;
  period?: number;
  minPercentage?: number;
  maxPercentage?: number;
  cargoFlowDirection: number;
}

interface FormValues {
  name: string;
  conditions: InputField[];
}

const CreateSenerio = ({
  isOpen,
  dropDownData,
  onClose,
  onSuccess,
  existingData,
}: IPROPS) => {
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      conditions: [
        {
          conditionId: 0,
          period: 0,
          minPercentage: 0,
          maxPercentage: 0,
          cargoFlowDirection: 0,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "conditions",
  });

 

  const watchConditions = watch("conditions");

  const getFilteredOptions = (index: number) => {
    return dropDownData?.filter((option: any) => {
      const value = String(option?.value || '').toLowerCase();
      return value === 'headhaul';
    });
    // const selected = watchConditions
    //   ?.filter((_, i) => i !== index)
    //   ?.map((c) => c.cargoFlowDirection);

    // return dropDownData?.filter(
    //   (option: any) =>
    //     !selected?.includes(option.value) ||
    //     option.value === watchConditions?.[index]?.cargoFlowDirection
    // );
  };

  const onSubmit = async (data: FormValues) => {
    if (data.conditions.length === 0) {
      setFormError("You must have at least one Scenario before saving.");
      return;
    }

    setFormError("");
    setIsBtnDisabled(true);

    const payload = {
      id: existingData?.scenarioId ?? 0, // use existing ID if editing
      name: data.name,
      isActive : existingData?.isActive,
      conditions: data.conditions.map((item) => ({
        conditionId: item.conditionId ?? 0, // Include ID for updates if needed
        period: item.period,
        minPercentage: item.minPercentage,
        maxPercentage: item.maxPercentage,
        cargoFlowDirectionId: item.cargoFlowDirection,
      })),
    };

    const response = await postAPI(
      `simulation-api/simulation/SaveModuleTriggerScenariosAsync`,
      payload
    );

    if (response > 0) {
      const successMessage = existingData
        ? "Scenario updated successfully"
        : "Scenario added successfully";

      toastEmitter(
        { className: "customClassName", title: successMessage },
        { type: "success" }
      );
      onSuccess();
      onClose();
    }

    setIsBtnDisabled(false);
    reset();
  };

  const cancelFun = () => {
    onClose();
    reset();
  };

  useEffect(() => {
    if (isOpen) {
      setIsBtnDisabled(false);
      setFormError("");

      if (existingData) {
        reset({
          name: existingData.scenarioName,
          conditions:
            existingData.conditions?.map((cond: any) => ({
              conditionId: cond.conditionId,
              period: cond.period,
              minPercentage: cond.minPercentage,
              maxPercentage: cond.maxPercentage,
              cargoFlowDirection: cond.cargoFlowDirectionId,
            })) ?? [],
        });
      } else if (dropDownData?.length) {
        const defaultDirection =
          dropDownData.find(
            (item: any) => item.label.toUpperCase() === "HEADHAUL"
          )?.value ?? 0;

        reset({
          name: "",
          conditions: [
            {
              conditionId: undefined,
              period: undefined,
              minPercentage: undefined,
              maxPercentage: undefined,
              cargoFlowDirection: defaultDirection,
            },
          ],
        });
      }
    }
  }, [isOpen, dropDownData, existingData, reset]);

  return (
    <form data-testid="create-senerio-form" onSubmit={handleSubmit(onSubmit)}>
      <Modal
        className="fullscreen-modal"
        open={isOpen}
        width="700px"
        height="auto"
        showCloseIcon={false}
        actions={{
          primaryAction: (
            <Button
              label="Submit"
              disabled={isBtnDisabled}
              fit="small"
              type="submit"
            />
          ),
          secondaryAction: (
            <Button
              label="Cancel"
              fit="small"
              data-testid="cancel"
              onClick={cancelFun}
              variant="outlined"
            />
          ),
        }}
      >
        <div>
          <h4 className="modal-header">
            {existingData ? "Edit Scenario" : "Create New Scenario"}
          </h4>
        </div>

        <div className="row">
          {formError && (
            <div className="col-md-12 mb-2">
              <div className="alert alert-danger">{formError}</div>
            </div>
          )}

          <div className="row mb-3">
            <div className="col-md-6">
              <Controller
                name="name"
                control={control}
                rules={{ required: "Scenario name is required" }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    variant="default"
                    type="text"
                    fit="small"
                    id="scenario-name"
                    label="Scenario Name"
                    onChange={onChange}
                    value={value ?? ""}
                    required
                    error={!!errors.name}
                    errorMessage={errors.name?.message?.toString()}
                  />
                )}
              />
            </div>
          </div>

          {fields.map((field, index) => (
            <React.Fragment key={field.id}>
              <div className="col-md-3 mb-3" style={{ width:"150px"}} >
              <div style={{ flex: 0.5}}>
              <Controller
                  name={`conditions.${index}.period`}
                  control={control}
                  rules={{
                    required: { value: true, message: "Period is required" },
                    min: { value: 0, message: "Minimum value is 0" },
                    max: { value: 99, message: "Maximum value is 99" },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      className="custom-input-width" 
                      variant="default"
                      type="number"
                      fit="small"
                      label="Period-Past months"
                      onChange={onChange}
                      value={value?.toString() || ""}
                      min={0}
                      max={99}
                      id={`period-${index}`}
                      error={!!errors.conditions?.[index]?.period}
                      errorMessage={errors.conditions?.[
                        index
                      ]?.period?.message?.toString()}
                      placeholder="Months"
                    />
                  )}
                />
              </div>

                
              </div>

              <div className="col-md-4 mb-3">
  <label className="form-label d-block" 
  style={{ fontSize: "0.88rem", marginBottom: "0rem" ,  marginTop: "-3px"}} >
    Percentage Utilization Between
    </label>
  <div className="d-flex gap-2 align-items-start">
    {/* Min Percentage */}
    <div style={{ flex: 0.40,marginTop:"2px" }}>
      <Controller
        name={`conditions.${index}.minPercentage`}
        control={control}
        rules={{
          required: {
            value: true,
            message: "Minimum percentage is required",
          },
          min: { value: 0, message: "Cannot be less than 0" },
          max: { value: 100, message: "Cannot exceed 100" },
          validate: (value) => {
            const max = watchConditions?.[index]?.maxPercentage;
            if (
              value !== undefined &&
              max !== undefined &&
              value > max
            ) {
              return "Min percentage cannot be greater than Max percentage";
            }
            return true;
          },
        }}
        render={({ field: { onChange, value } }) => (
          <Input
          className="custom-input-margin " 
            variant="default"
            type="number"
            fit="small"
            // label="Min"
            onChange={onChange}
            value={value?.toString() || ""}
            min={0}
            max={100}
            id={`min-${index}`}
            error={!!errors.conditions?.[index]?.minPercentage}
            errorMessage={errors.conditions?.[index]?.minPercentage?.message?.toString()}
          />
        )}
      />
    </div>

    <label style={{ fontSize: "0.85rem", marginTop: "0.80rem" }} > %  and</label>

       
    {/* Max Percentage */}
    <div style={{ flex: 0.40,marginTop:"2px" }}>
      <Controller
        name={`conditions.${index}.maxPercentage`}
        control={control}
        rules={{
          required: {
            value: true,
            message: "Maximum percentage is required",
          },
          min: { value: 0, message: "Cannot be less than 0" },
          max: { value: 100, message: "Cannot exceed 100" },
          validate: (value) => {
            const min = watchConditions?.[index]?.minPercentage;
            if (
              value !== undefined &&
              min !== undefined &&
              value < min
            ) {
              return "Max percentage cannot be less than Min percentage";
            }
            return true;
          },
        }}
        render={({ field: { onChange, value } }) => (
          <Input
            variant="default"
            className="custom-input-margin" 
            type="number"
            fit="small"
            // label="Max"
            onChange={onChange}
            value={value?.toString() || ""}
            min={0}
            max={100}
            id={`max-${index}`}
            error={!!errors.conditions?.[index]?.maxPercentage}
            errorMessage={errors.conditions?.[index]?.maxPercentage?.message?.toString()}
          />
        )}
      />
    </div>
    <label style={{ fontSize: "0.90rem", marginTop: "0.80rem"}} > % </label>
  </div>
</div>


<div className="col-md-3 mb-3" style={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
      <Controller
        name={`conditions.${index}.cargoFlowDirection`}
        control={control}
        rules={{
          validate: (value) => {
            if (value === undefined || value === null || value === 0) {
              return "Direction is required";
            }
            return true;
          },
        }}
        render={({ field: { onChange, value } }) => (
          <ReactSelect
            hintPosition="left"
            fit="small"
            label="Direction"
            isSearchable
            options={getFilteredOptions(index)}
            value={dropDownData?.find((opt) => opt.value === value)}
            onChange={(e) => onChange(e?.value)}
            orientation="vertical"
            suggestType="static"
            variant="default"
            id={`direction-${index}`}
            name={`direction-${index}`}
            error={!!errors.conditions?.[index]?.cargoFlowDirection}
            errorMessage={errors.conditions?.[index]?.cargoFlowDirection?.message?.toString()}
          />
        )}
      />
    </div>

              {fields.length > 1 && (
                <div className="col-md-1 d-flex justify-content-center align-items-center">
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent"
                    onClick={() => remove(index)}
                  >
                    <Icon name="trash" className="value thrash-icon" />
                  </button>
                </div>
              )}
            </React.Fragment>
          ))}

          {/* <div className="col-md-12">
            {fields.length < dropDownData.length && (
              <span
                className="add-more"
                onClick={() =>
                  append({
                    period: undefined,
                    minPercentage: undefined,
                    maxPercentage: undefined,
                    cargoFlowDirection: 0,
                  })
                }
              >
                + Add more
              </span>
            )}
          </div> */}
        </div>
      </Modal>
    </form>
  );
};

export default CreateSenerio;
